import Button from '@/components/base/button/button';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import Modal from '@/components/base/modal/modal';
import { useSearchProductsQuery } from '@/features/navigation/api/query/search-products';
import { ProductNotFound } from '@/features/navigation/components/search/product-not-found';
import { categorizeProducts } from '@/features/navigation/hooks/categorize-products';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useTranslations } from 'next-intl';
import { type Control, Controller } from 'react-hook-form';
import { CategoriesList } from './categories-list';
import { ProductsList } from './products-list';
import type { SearchFormValues } from './search';
import { SearchInput } from './search-input';

interface Props {
  handleChangeQuery: (query: string) => void;
  debouncedQuery: string;
  control: Control<SearchFormValues>;
  toggleFocus: (focused: boolean) => void;
  isModalOpen: boolean;
  handleOpenModal: () => void;
  handleCloseModal: () => void;
}

export const MobileSearch = ({
  handleChangeQuery,
  debouncedQuery,
  control,
  toggleFocus,
  handleCloseModal,
  handleOpenModal,
  isModalOpen,
}: Props) => {
  const t = useTranslations('navigation');

  const { data, isLoading } = useSearchProductsQuery(debouncedQuery);

  const { categories, products } = categorizeProducts(data?.hits ?? []);

  return (
    <>
      <Button
        variant="tertiary-no-frame"
        className="m:hidden h-full l:h-clickable l:w-auto w-clickable l:px-s"
        data-ui="mobile-search-button"
        aria-expanded={isModalOpen}
        onClick={handleOpenModal}
      >
        <PhosphorIcon
          Icon={MagnifyingGlass}
          variant="regular"
          className="icon-m s:icon-l w-auto fill-strong"
        />
        <span className="sr-only">{t('search.open_search_dialog')}</span>
      </Button>
      <Modal
        title={t('search_for_gift')}
        setIsOpen={handleCloseModal}
        isOpen={isModalOpen}
        fullScreen
        className="!m-0"
      >
        <div className="h-full w-full px-xs s:px-[30px] xs:px-s py-m">
          <Controller
            control={control}
            name="query"
            render={({ field: { onBlur, onChange, value } }) => (
              <SearchInput
                autoFocus={isModalOpen}
                onBlur={(e) => {
                  onBlur();
                  handleChangeQuery(e.target.value);
                  toggleFocus(false);
                }}
                onChange={(e) => {
                  onChange(e);
                  handleChangeQuery(e.target.value);
                  toggleFocus(true);
                }}
                onFocus={() => {
                  return;
                }}
                onReset={handleCloseModal}
                value={value}
              />
            )}
          />
          <div className="mt-xxs" aria-live="polite">
            <ProductsList products={products} isLoading={isLoading} />
            <CategoriesList categories={categories} isLoading={isLoading} />
            {data && data?.hitsCount <= 0 && <ProductNotFound />}
          </div>
        </div>
      </Modal>
    </>
  );
};
